import React, { useMemo, useState } from 'react';
import { Table } from 'antd';
import PropTypes from 'prop-types';
import { DateTime } from 'luxon';

import OnTraccrButton from '../../../../common/buttons/OnTraccrButton';
import {
  getEquipmentCostColumns,
  getMaterialCostColumns,
  getUncategorizedInvoiceColumns,
} from '../../../ProjectProgress/ProgressColumns';
import BudgetTrackingCollapse from '../BudgetTrackingCollapse';
import BudgetTrackingEmptyState from '../BudgetTrackingEmptyState';

const TABS = [
  { key: 'progress', title: 'Progress' },
  { key: 'budget', title: 'Budget' },
];

export default function CostTrackingUncategorized({
  uncategorizedInvoices,
  uncategorizedMaterialUpdates,
  uncategorizedEquipmentUpdates,
  uncategorizedMaterialBudgetUpdates,
  uncategorizedEquipmentBudgetUpdates,
  onUncategorizedClick,
  projectInvoices,
}) {
  const uncategorizedInvoiceCols = useMemo(() => getUncategorizedInvoiceColumns(), []);
  const uncategorizedMaterialsCols = useMemo(() => getMaterialCostColumns(), []);
  const uncategorizedEquipmentCols = useMemo(() => getEquipmentCostColumns(), []);

  const latestInvoiceSyncTimestamp = useMemo(() => {
    const invoicesFromQBO = projectInvoices
      .filter((i) => !!i.fromQBOTimestamp)
      .sort((a, b) => b.fromQBOTimestamp - a.fromQBOTimestamp);

    const latest = invoicesFromQBO?.[0]?.fromQBOTimestamp ?? null;

    return latest
      ? DateTime.fromMillis(latest).toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS)
      : null;
  }, [projectInvoices]);

  const invoiceTitleSuffix = latestInvoiceSyncTimestamp ? ` (Last Sync: ${latestInvoiceSyncTimestamp})` : '';

  const hasProgressUncategorized = (
    uncategorizedMaterialUpdates.length > 0
    || uncategorizedEquipmentUpdates.length > 0
  );
  const hasBudgetUncategorized = (
    uncategorizedMaterialBudgetUpdates.length > 0
    || uncategorizedEquipmentBudgetUpdates.length > 0
  );
  const hasMaterialOrEquipmentSection = hasProgressUncategorized || hasBudgetUncategorized;

  const [userSelectedTab, setUserSelectedTab] = useState(null);
  const selectedTab = userSelectedTab
    ?? (hasProgressUncategorized || !hasBudgetUncategorized ? 'progress' : 'budget');

  const isBudgetTab = selectedTab === 'budget';
  const materialUpdates = isBudgetTab
    ? uncategorizedMaterialBudgetUpdates
    : uncategorizedMaterialUpdates;
  const equipmentUpdates = isBudgetTab
    ? uncategorizedEquipmentBudgetUpdates
    : uncategorizedEquipmentUpdates;
  const isMaterialEquipmentEmpty = !materialUpdates.length && !equipmentUpdates.length;

  return (
    <div id="uncategorized-costs" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {uncategorizedInvoices.length > 0 && (
        <BudgetTrackingCollapse
          panelKey="uncategorized-invoices"
          header={<span className="budget-tracking-section-title">Uncategorized Invoice Costs{invoiceTitleSuffix}</span>}
        >
          <Table
            style={{ marginTop: 16 }}
            className="budget-tracking-table"
            size="small"
            columns={uncategorizedInvoiceCols}
            dataSource={uncategorizedInvoices}
            pagination={false}
            rowClassName="file-table-row"
            scroll={{ x: 'max-content' }}
            onRow={onUncategorizedClick}
          />
        </BudgetTrackingCollapse>
      )}
      {hasMaterialOrEquipmentSection && (
        <BudgetTrackingCollapse
          panelKey="uncategorized-material-equipment"
          header={<span className="budget-tracking-section-title">Uncategorized Material & Equipment Costs</span>}
        >
          <div className="cost-tracking-budget-tabs" style={{ marginTop: 24, marginBottom: 16 }}>
            {TABS.map((tab) => (
              <OnTraccrButton
                key={tab.key}
                className={selectedTab === tab.key
                  ? 'cost-tracking-budget-tab-selected'
                  : 'cost-tracking-budget-tab'}
                onClick={() => setUserSelectedTab(tab.key)}
                title={tab.title}
              />
            ))}
          </div>
          {isMaterialEquipmentEmpty ? (
            <BudgetTrackingEmptyState
              text={isBudgetTab
                ? 'No uncategorized budget updates'
                : 'No uncategorized costs'}
            />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {materialUpdates.length > 0 && (
                <div>
                  <span className="budget-tracking-section-title">Uncategorized Material Costs</span>
                  <Table
                    style={{ marginTop: 16 }}
                    className="budget-tracking-table"
                    size="small"
                    columns={uncategorizedMaterialsCols}
                    dataSource={materialUpdates}
                    pagination={false}
                    scroll={{ x: 'max-content' }}
                    rowClassName="file-table-row"
                  />
                </div>
              )}
              {equipmentUpdates.length > 0 && (
                <div>
                  <span className="budget-tracking-section-title">Uncategorized Equipment Costs</span>
                  <Table
                    style={{ marginTop: 16 }}
                    className="budget-tracking-table"
                    size="small"
                    columns={uncategorizedEquipmentCols}
                    dataSource={equipmentUpdates}
                    pagination={false}
                    scroll={{ x: 'max-content' }}
                    rowClassName="file-table-row"
                  />
                </div>
              )}
            </div>
          )}
        </BudgetTrackingCollapse>
      )}
    </div>
  );
}

/* eslint-disable react/forbid-prop-types */
CostTrackingUncategorized.propTypes = {
  uncategorizedInvoices: PropTypes.array,
  uncategorizedMaterialUpdates: PropTypes.array,
  uncategorizedEquipmentUpdates: PropTypes.array,
  uncategorizedMaterialBudgetUpdates: PropTypes.array,
  uncategorizedEquipmentBudgetUpdates: PropTypes.array,
  onUncategorizedClick: PropTypes.func,
  projectInvoices: PropTypes.array,
};

CostTrackingUncategorized.defaultProps = {
  uncategorizedInvoices: [],
  uncategorizedMaterialUpdates: [],
  uncategorizedEquipmentUpdates: [],
  uncategorizedMaterialBudgetUpdates: [],
  uncategorizedEquipmentBudgetUpdates: [],
  onUncategorizedClick: () => {},
  projectInvoices: [],
};
